'use client';

import axios from 'axios';

const FINNHUB_API_KEY = process.env.NEXT_PUBLIC_FINNHUB_API_KEY || '';
const FINNHUB_BASE_URL = 'https://finnhub.io/api/v1';

// Cache for API responses
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute

export interface FinnhubQuote {
  c: number; // Current price
  d: number; // Change
  dp: number; // Percent change
  h: number; // High price of the day
  l: number; // Low price of the day
  o: number; // Open price of the day
  pc: number; // Previous close price
  t: number; // Timestamp
}

export interface FinnhubStockQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  timestamp: number;
}

export interface FinnhubCompanyProfile {
  country: string;
  currency: string;
  exchange: string;
  ipo: string;
  marketCapitalization: number;
  name: string;
  phone: string;
  shareOutstanding: number;
  ticker: string;
  weburl: string;
  logo: string;
  finnhubIndustry: string;
}

export interface FinnhubNewsItem {
  category: string;
  datetime: number;
  headline: string;
  id: number;
  image: string;
  related: string;
  source: string;
  summary: string;
  url: string;
}

export const finnhubService = {
  /**
   * Fetch real-time quote for a single symbol
   */
  async getQuote(symbol: string): Promise<FinnhubStockQuote | null> {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    const cacheKey = `quote_${symbol}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/quote`, {
        params: {
          symbol,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      const quote = response.data as FinnhubQuote;
      
      // Validate response - Finnhub returns all zeros for invalid/unsupported symbols
      if (!quote || quote.c === 0 || quote.d === null || quote.dp === null) {
        console.warn(`Finnhub: Symbol ${symbol} not supported or invalid (returned zeros)`);
        return null;
      }

      const stockQuote: FinnhubStockQuote = {
        symbol,
        price: quote.c,
        change: quote.d,
        changePercent: quote.dp,
        high: quote.h,
        low: quote.l,
        open: quote.o,
        previousClose: quote.pc,
        timestamp: quote.t,
      };

      cache.set(cacheKey, { data: stockQuote, timestamp: Date.now() });
      return stockQuote;
    } catch (error) {
      console.error(`Finnhub API error for ${symbol}:`, error);
      return null;
    }
  },

  /**
   * Fetch quotes for multiple symbols
   * Finnhub free tier: 60 calls/minute
   */
  async getMultipleQuotes(symbols: string[]): Promise<Map<string, FinnhubStockQuote>> {
    const results = new Map<string, FinnhubStockQuote>();
    
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return results;
    }

    try {
      // Fetch all quotes in parallel (Finnhub allows 60 calls/min)
      const promises = symbols.map(async (symbol) => {
        try {
          const quote = await this.getQuote(symbol);
          if (quote) {
            results.set(symbol, quote);
          }
        } catch (error) {
          console.error(`Error fetching Finnhub quote for ${symbol}:`, error);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error('Finnhub multiple quotes error:', error);
    }

    return results;
  },

  /**
   * Get company profile
   */
  async getCompanyProfile(symbol: string): Promise<FinnhubCompanyProfile | null> {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    const cacheKey = `profile_${symbol}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < 3600000) { // 1 hour cache for profiles
      return cached.data;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/stock/profile2`, {
        params: {
          symbol,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      const profile = response.data as FinnhubCompanyProfile;
      cache.set(cacheKey, { data: profile, timestamp: Date.now() });
      return profile;
    } catch (error) {
      console.error(`Finnhub company profile error for ${symbol}:`, error);
      return null;
    }
  },

  /**
   * Get market news
   */
  async getMarketNews(category: string = 'general'): Promise<FinnhubNewsItem[]> {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return [];
    }

    const cacheKey = `news_${category}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutes cache for news
      return cached.data;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/news`, {
        params: {
          category,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      const news = response.data as FinnhubNewsItem[];
      cache.set(cacheKey, { data: news, timestamp: Date.now() });
      return news;
    } catch (error) {
      console.error('Finnhub market news error:', error);
      return [];
    }
  },

  /**
   * Get company news
   */
  async getCompanyNews(symbol: string, fromDate: string, toDate: string): Promise<FinnhubNewsItem[]> {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return [];
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/company-news`, {
        params: {
          symbol,
          from: fromDate,
          to: toDate,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      return response.data as FinnhubNewsItem[];
    } catch (error) {
      console.error(`Finnhub company news error for ${symbol}:`, error);
      return [];
    }
  },

  /**
   * Search for symbols
   */
  async searchSymbols(query: string) {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/search`, {
        params: {
          q: query,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error('Finnhub symbol search error:', error);
      return null;
    }
  },

  /**
   * Get forex rates
   */
  async getForexRates(base: string = 'USD'): Promise<any> {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    const cacheKey = `forex_${base}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/forex/rates`, {
        params: {
          base,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Finnhub forex rates error:', error);
      return null;
    }
  },

  /**
   * Get crypto candles (OHLC data)
   */
  async getCryptoCandles(
    symbol: string,
    resolution: string = 'D',
    from: number,
    to: number
  ) {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/crypto/candle`, {
        params: {
          symbol,
          resolution,
          from,
          to,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error('Finnhub crypto candles error:', error);
      return null;
    }
  },

  /**
   * Get market status
   */
  async getMarketStatus(exchange: string = 'US') {
    if (!FINNHUB_API_KEY || FINNHUB_API_KEY === 'your-finnhub-api-key-here') {
      console.warn('Finnhub API key not configured');
      return null;
    }

    try {
      const response = await axios.get(`${FINNHUB_BASE_URL}/stock/market-status`, {
        params: {
          exchange,
          token: FINNHUB_API_KEY,
        },
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error('Finnhub market status error:', error);
      return null;
    }
  },
};